import React, { ReactNode, useCallback, useEffect, useState, useRef, useReducer } from 'react'
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams
} from 'react-router-dom'
import { ChakraProvider, Spinner, createSystem, defineConfig, defaultConfig } from '@chakra-ui/react'
import { ThemeProvider } from '@emotion/react'
import { AppStore, keycloak, OperationConfig, RemoteOperation, useVersionCheck, setRouterRefs } from 'stylewhere/shared'
import {
  Box,
  ConfirmModal,
  Modal,
  FeedbackModal,
  UpdateNotification,
  DeviceManagerUpdateNotification,
  ToastContainer
} from 'stylewhere/components'
import { ModalType } from 'stylewhere/types'
import { theme, chakraTheme } from 'stylewhere/theme'

// Crea un sistema personalizzato con i colori blu definiti in chakraTheme
const customSystem = createSystem(
  defaultConfig,
  defineConfig({
    theme: {
      tokens: {
        colors: {
          blue: {
            50: { value: chakraTheme.colors.blue['50'] },
            100: { value: chakraTheme.colors.blue['100'] },
            200: { value: chakraTheme.colors.blue['200'] },
            300: { value: chakraTheme.colors.blue['300'] },
            400: { value: chakraTheme.colors.blue['400'] },
            500: { value: chakraTheme.colors.blue['500'] },
            600: { value: chakraTheme.colors.blue['600'] },
            700: { value: chakraTheme.colors.blue['700'] },
            800: { value: chakraTheme.colors.blue['800'] },
            900: { value: chakraTheme.colors.blue['900'] }
          }
        }
      }
    }
  })
)
import { Routes } from 'stylewhere/pages'
import 'react-virtualized/styles.css'

// Componente interno per inizializzare i riferimenti del router
const RouterInitializer: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    setRouterRefs(navigate, location)
  }, [navigate, location])

  return <>{children}</>
}

// Wrapper per componenti class che usano RouteComponentProps
const RouteWrapper: React.FC<{ Component: React.ComponentType<any> }> = ({ Component }) => {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const match = { params: params as Record<string, string> }
  const history = {
    push: (path: string, state?: any) => navigate(path, { state }),
    replace: (path: string, state?: any) => navigate(path, { replace: true, state })
  }

  return <Component match={match} location={location} history={history} />
}

const App: React.FC = () => {
  const [loading, setloading] = useState<boolean>(true)
  const [showConfirm, setshowConfirm] = useState<boolean>(false)
  const [showFeedback, setshowFeedback] = useState<boolean>(false)
  const [modals, setmodals] = useState<ModalType[]>([])
  const [authenticated, setauthenticated] = useState<boolean>(false)
  const [, forceRender] = useReducer((count: number) => count + 1, 0)
  const [operations, setOperations] = useState<OperationConfig[]>()
  const forceUpdate = useCallback(() => forceRender(), [])
  const hideModalById = useCallback((currentModals: ModalType[], id: string): ModalType[] => {
    const modalIndex = currentModals.findIndex((modal) => modal.id === id)
    if (modalIndex === -1) {
      return currentModals
    }
    const updatedModals = [...currentModals]
    updatedModals[modalIndex] = { ...updatedModals[modalIndex], visible: false }
    return updatedModals.filter((modal) => modal.visible)
  }, [])

  const sessionExpiredTimeout = useRef<NodeJS.Timeout | null>(null)
  const keycloakInitialized = useRef(false)
  const appStoreLoaded = useRef(false)

  // Workaround per hot reload
  if (operations !== undefined) {
    RemoteOperation.operations = operations
  }

  // Inizializza il controllo versione (solo frontend)
  useVersionCheck()

  useEffect(() => {
    async function init() {
      AppStore.toggleConfirmModalHandler = (show) => setshowConfirm(show)
      AppStore.toggleFeebackModalHandler = (show) => setshowFeedback(show)
      AppStore.openModal = (modal: ModalType) => {
        setmodals((currentModals) => [...currentModals, modal])
      }
      AppStore.closeModal = (id: string) => {
        setmodals((currentModals) => hideModalById(currentModals, id))
      }
      AppStore.reloadRouting = () => forceUpdate()
    }
    init()
    return () => {}
  }, [forceUpdate, hideModalById, modals])

  useEffect(() => {
    const onAuthSuccess = async () => {
      !!sessionExpiredTimeout.current && clearTimeout(sessionExpiredTimeout.current)
      sessionExpiredTimeout.current = null

      if (!appStoreLoaded.current) {
        appStoreLoaded.current = true
        await AppStore.loadInitialData()
        setauthenticated(true)
        setloading(false)
        setOperations(RemoteOperation.operations)
      }
    }

    // Usa ref invece di uno stato perché keykloack.init schianta se chiamato due volte,
    // come avviene sempre con StrictMode
    if (!keycloakInitialized.current) {
      keycloakInitialized.current = true

      keycloak.onAuthSuccess = () => {
        onAuthSuccess()
      }

      const onError = () => {
        sessionExpiredTimeout.current = setTimeout(() => {
          setauthenticated(false)
          setloading(false)
        }, 5000)
      }
      keycloak.onAuthError = () => onError()
      keycloak.onAuthRefreshError = () => onError()

      keycloak.onTokenExpired = () => {
        // Refresh expired token
        keycloak.updateToken(5)
      }
      keycloak.onAuthRefreshSuccess = () => {
        // Refresh appstore a resume fetching notifications
        AppStore.loadAuthToken()
      }
      keycloak.init({ onLoad: 'login-required', checkLoginIframe: false })
    }
  }, [setauthenticated])

  const paths = Object.keys(Routes)
  const activeRoutes = paths.filter((path) =>
    authenticated ? !Routes[path].public || Routes[path].private : Routes[path].public
  )

  return (
    <ChakraProvider value={customSystem}>
      <ThemeProvider theme={theme}>
        {loading && (
          <Box flex center vcenter>
            <Spinner color="#e0e0e0" size="xl" />
          </Box>
        )}

        {!loading && (
          <>
            <BrowserRouter>
              <RouterInitializer>
                <RouterRoutes>
                  {activeRoutes.map((path) => {
                    const Component = Routes[path].component as React.ComponentType<any>
                    return <Route path={path} element={<RouteWrapper Component={Component} />} key={path} />
                  })}
                  <Route path="*" element={<Navigate to={authenticated ? '/' : '/login'} replace />} />
                </RouterRoutes>

                <UpdateNotification />
                <DeviceManagerUpdateNotification />
                <ToastContainer />
              </RouterInitializer>
            </BrowserRouter>

            {modals.map((modal) => {
              if ('modal' in modal) {
                // Clone the React element and override the 'visible' prop with modal.visible
                // This ensures that when modal.visible changes, the component re-renders with the new value
                if (!modal.visible) {
                  return null
                }
                if (React.isValidElement(modal.modal)) {
                  return React.cloneElement(modal.modal as React.ReactElement<any>, { visible: modal.visible })
                }
                return modal.modal as ReactNode
              }

              return (
                <Modal
                  {...modal}
                  key={modal.id}
                  onClose={() => {
                    setmodals((currentModals) => {
                      const updatedModals = currentModals.map((m) => (m.id === modal.id ? { ...m, visible: false } : m))
                      const filteredModals = updatedModals.filter((m) => m.visible)
                      return filteredModals
                    })
                    modal?.onClose?.()
                  }}
                >
                  {typeof modal.body === 'function' ? modal.body() : modal.body}
                </Modal>
              )
            })}

            {showConfirm && (
              <ConfirmModal
                title={AppStore.confirmModalParams.title}
                subtitle={AppStore.confirmModalParams.message}
                onClose={() => {
                  AppStore.confirmModalParams?.onCancel?.()
                  setshowConfirm(false)
                }}
                onConfirm={() => {
                  AppStore.confirmModalParams?.onConfirm()
                  setshowConfirm(false)
                }}
                labelClose={AppStore.confirmModalParams.labelClose}
                labelConfirm={AppStore.confirmModalParams.labelConfirm}
                showDetailForProduct={AppStore.confirmModalParams.showDetailForProduct}
              />
            )}
            {showFeedback && (
              <FeedbackModal
                title={AppStore.feedbackModalParam.title}
                subtitle={AppStore.feedbackModalParam.message}
                onClose={() => {
                  AppStore.feedbackModalParam?.onCancel?.()
                  setshowFeedback(false)
                }}
                btnCloseModal={AppStore.feedbackModalParam.btnCloseModal}
              />
            )}
          </>
        )}
      </ThemeProvider>
    </ChakraProvider>
  )
}

export default App
